"use client"

import styled from "@emotion/styled"
import { TrimOption } from "@eurodrive/utils"
import { Typography } from "@mui/material"
import { Dispatch, SetStateAction, useEffect } from "react"
import { Config, formatPrice } from "./CarConfiguration"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TrimCard = styled.label<{ selected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid ${({ selected }) => (selected ? "#000000" : "#ECE9E9")};
  border-radius: 8px;
  background-color: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #000000;
  }
`

const HiddenRadio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`

export default function TrimStage({
  markComplete,
  trims,
  config,
  setConfig,
}: {
  markComplete: () => void
  trims: TrimOption[]
  config: Config
  setConfig: Dispatch<SetStateAction<Config>>
}) {
  // Auto-select when there's only one trim option
  useEffect(() => {
    if (trims.length === 1) {
      if (config.trim === null) {
        setConfig((prev) => ({ ...prev, trim: 0 }))
      }
      markComplete()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (index: number) => {
    setConfig((prev) => ({
      ...prev,
      trim: index,
      // Each trim has its own set of available colors, so a previously
      // selected color may not exist for the newly selected trim
      color: null,
    }))
    markComplete()
  }

  // Base price is the cheapest trim (trims are sorted ascending by price)
  const basePrice = trims.length > 0 ? trims[0].price : 0

  const getTrimDelta = (trim: TrimOption) => trim.price - basePrice

  const getAriaLabel = (trim: TrimOption) => {
    const delta = getTrimDelta(trim)
    const priceText = delta === 0
      ? "כלול במחיר"
      : `תוספת ${formatPrice(delta)}`
    return `${trim.trimLevelDisplay}, ${priceText}`
  }

  return (
    <StyledLayout>
      <Typography variant="h2" component="h2">
        בחירת רמת גימור
      </Typography>
      <ContentLayout role="radiogroup">
        {trims && trims.length > 0 ? (
          trims.map((trim, index) => {
            const delta = getTrimDelta(trim)
            return (
              <TrimCard key={index} selected={config.trim === index}>
                <HiddenRadio
                  type="radio"
                  name="trim"
                  value={index}
                  checked={config.trim === index}
                  onChange={() => handleSelect(index)}
                  aria-label={getAriaLabel(trim)}
                />
                <Typography variant="body1">{trim.trimLevelDisplay}</Typography>
                <Typography variant="body1" aria-hidden="true">
                  {delta === 0
                    ? "כלול במחיר"
                    : `+${formatPrice(delta)}`}
                </Typography>
              </TrimCard>
            )
          })
        ) : (
          <Typography
            variant="body3"
            style={{ textAlign: "center", padding: "20px", color: "#666" }}
          >
            לא נמצאו אפשרויות גימור
          </Typography>
        )}
      </ContentLayout>
    </StyledLayout>
  )
}
